import { useMemo, useRef } from 'react'
import { MessageSquare } from 'lucide-react'
import { AgentApp } from '../../../agent/App'
import type { AgentConfig } from '../../../agent/types'
import { useAceSession } from '../../../agent/useAceSession'
import { apiUrl } from '@/runtime'

interface Props {
  /** Server-side persisted thread_id. Empty string = no thread yet (SDK creates
   *  lazily on first send). Non-empty = reconnect to existing thread. */
  threadId?: string | null
  caseId: string
  selectedBlockIds: string[]
  onArtifactRef?: (artifactId: string, docVersion: number, changedBlockIds: string[]) => void
  /** Called once the SDK send function is available; store the fn in a ref to
   *  dispatch block-menu messages from outside the SDK provider tree. */
  onRegisterSend?: (fn: (text: string) => void) => void
  /** Called whenever the SDK conversation-flow state changes; lets the route
   *  derive freeze-during-agent-run state without using SDK hooks itself. */
  onRegisterFlow?: (state: { isLoading: boolean }) => void
  /** Called the first time the SDK creates an ACE thread. Parent persists the
   *  id so subsequent page loads reconnect to the same conversation. */
  onThreadCreated?: (threadId: string) => void
}

const SUGGESTED_PROMPTS = [
  'Make it more concise overall.',
  'Make the tone more formal across the board.',
  'Group sections by asset class.',
]

/**
 * Editor chat panel. Wraps the existing <AgentApp> with editor-specific config:
 *  - assistant id pinned to the proposal editor assistant (from ACE session
 *    payload — single source of truth on the server, configurable via env)
 *  - selection chips merged into the composer's outgoing payload
 */
export function ChatPanel({
  threadId,
  caseId,
  selectedBlockIds,
  onRegisterSend,
  onRegisterFlow,
  onThreadCreated,
}: Props) {
  const session = useAceSession()
  // Resolve the editor assistant id from the session payload. Falls back to
  // the dashboard's default when the session is still loading or the field is
  // missing (defensive — the field should always be present after the BE
  // change, but FE shouldn't crash if BE is older).
  const editorAssistant =
    session.status === 'ready'
      ? (session.session.proposal_editor_assistant ?? session.session.default_assistant)
      : undefined
  const advisorCode = session.status === 'ready' ? session.session.advisor_code : undefined

  // Stable thread metadata: forwarded only on the first send in a brand-new
  // thread; the SDK persists it as ACE Thread.metadata. Used purely for
  // BE/FE bookkeeping ("which case does this thread belong to") — NOT as a
  // transport for LLM context, since ACE's assistant-config markdown
  // processor mangles the Jinja that would read it. The actual LLM context
  // is delivered via the `seedContext` hidden message below.
  const threadMetadata = useMemo<Record<string, unknown> | undefined>(() => {
    if (!advisorCode) return undefined
    return { proposal_editor: { caseId, advisor_code: advisorCode } }
  }, [advisorCode, caseId])

  // Hidden seed context: prepended as a hidden user message to the FIRST
  // send of a brand-new thread. The LLM reads it (verified — ACE's
  // get_messages_for_agent does not filter on `hidden`); the chat UI
  // suppresses it. The system prompt instructs the model to parse case_id
  // out of this line and use it for every tool call.
  const seedContext = useMemo<string | undefined>(() => {
    if (!advisorCode) return undefined
    return `[ctx] case_id=${caseId} advisor_code=${advisorCode}`
  }, [advisorCode, caseId])

  // Per-message metadata: recomputed at send time so the latest selection
  // travels with each user turn. The ref + getter pattern avoids re-rendering
  // AgentApp every time selection changes.
  const selectionRef = useRef<string[]>(selectedBlockIds)
  selectionRef.current = selectedBlockIds
  const buildMessageMetadata = useMemo(
    () => (): Record<string, unknown> | undefined => ({
      proposal_editor: { caseId, selected_block_ids: selectionRef.current },
    }),
    [caseId],
  )

  const config: AgentConfig = {
    runnerUrl: apiUrl('/api/agent/runner'),
    sessionRoot: apiUrl('/api/agent/session'),
    hasAgentAuth: true,
    authSource: 'banker',
    model: 'editor-v1',
    allowedTools: ['proposal_doc_get', 'proposal_doc_update', 'proposal_doc_dossier'],
    initialSessionId: threadId || undefined,
    suggestedPrompts: SUGGESTED_PROMPTS,
    assistantName: editorAssistant,
  }
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 rounded-lg border border-border bg-card p-3">
      <header className="flex items-center justify-between gap-2 px-1">
        <div className="flex items-center gap-2 text-body-emphasis text-foreground">
          <MessageSquare className="size-4 text-primary" aria-hidden />
          Chat AI
        </div>
        <span className="text-caption text-muted-foreground">Contesto: caso {caseId}</span>
      </header>
      <div className="relative min-h-0 flex-1 overflow-hidden rounded-md border border-border bg-background">
        <AgentApp
          config={config}
          onRegisterSend={onRegisterSend}
          onRegisterFlow={onRegisterFlow}
          onThreadCreated={onThreadCreated}
          threadMetadata={threadMetadata}
          buildMessageMetadata={buildMessageMetadata}
          seedContext={seedContext}
        />
      </div>
    </div>
  )
}
